/**
 * O resultado do retorno da autorização de UMA mensagem da thread (`TP-056`, T6, D8 b).
 *
 * A pessoa olha a carta da SIAI (a imagem vem no e-mail; o `.eml` abre ao lado) e marca o que a operadora decidiu.
 * Marcar não transita o Caso: quem confirma é a lista de retornos, um clique depois. A escolha da pessoa vale mais
 * que a leitura do agente, e um retorno já confirmado ou descartado não muda mais.
 */

import Button from '@mui/material/Button';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { marcarResultadoDoRetorno } from '../../shared/retornos/api';
import type { ResultadoDoRetorno as Resultado } from '../../shared/retornos/tipos';
import {
  RESULTADOS_PARA_ESCOLHA,
  ROTULOS_DO_RESULTADO,
  mensagemDeErroDoRetorno,
  rotuloDaOrigem,
} from '../../shared/retornos/vocabulario';
import type { MensagemDaTriagem } from '../../shared/triagem/tipos';
import { chavesDeTriagem } from './api/filaDeTriagem';

export function ResultadoDoRetorno({
  ticketId,
  mensagem,
}: {
  ticketId: string;
  mensagem: MensagemDaTriagem;
}): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();
  const retorno = mensagem.retorno;
  const [escolha, definirEscolha] = useState<Resultado>(retorno?.resultado ?? 'Authorized');

  const marcar = useMutation({
    mutationFn: (resultado: Resultado) => marcarResultadoDoRetorno(ticketId, mensagem.id, resultado),
    onSuccess: () => {
      void clienteDeConsultas.invalidateQueries({ queryKey: chavesDeTriagem.detalhe(ticketId) });
      void clienteDeConsultas.invalidateQueries({ queryKey: ['retornos'] });
      void clienteDeConsultas.invalidateQueries({ queryKey: ['casos'] });
    },
  });

  const decidido = retorno?.decisao != null;

  return (
    <section aria-label="Resultado do retorno da autorização" className="space-y-2">
      <p className="text-xs text-[var(--tinta-2)]">Resultado do retorno da autorização nesta mensagem</p>
      <p className="text-sm">
        {retorno === null ? (
          'Nenhum resultado marcado.'
        ) : (
          <>
            <strong>{ROTULOS_DO_RESULTADO[retorno.resultado]}</strong> ·{' '}
            {rotuloDaOrigem(retorno.origem, retorno.confianca)}
            {retorno.decisao === 'Confirmed' ? ' · confirmado' : null}
            {retorno.decisao === 'Discarded' ? ' · descartado' : null}
          </>
        )}
      </p>

      {decidido ? (
        <p className="text-xs text-[var(--tinta-2)]">Retorno já decidido: o resultado não muda mais.</p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-sm">
            <span className="sr-only">Resultado da carta</span>
            <select
              aria-label="Resultado da carta"
              value={escolha}
              onChange={(evento) => {
                definirEscolha(evento.target.value as Resultado);
              }}
            >
              {RESULTADOS_PARA_ESCOLHA.map((r) => (
                <option key={r} value={r}>
                  {ROTULOS_DO_RESULTADO[r]}
                </option>
              ))}
            </select>
          </label>
          <Button
            size="small"
            variant="outlined"
            disabled={marcar.isPending}
            onClick={() => {
              marcar.mutate(escolha);
            }}
          >
            Marcar resultado
          </Button>
        </div>
      )}

      {marcar.isSuccess ? (
        <p role="status" className="text-xs">
          Resultado marcado: {ROTULOS_DO_RESULTADO[marcar.data.resultado]}. Confirme no caso, em Retornos da
          autorização.
        </p>
      ) : null}
      {marcar.isError ? (
        <p role="alert" aria-live="polite" className="text-[0.8125rem] text-[var(--roxo)]">
          {mensagemDeErroDoRetorno(marcar.error)}
        </p>
      ) : null}
    </section>
  );
}
